import React from 'react';
import { NavLink } from 'react-router-dom';

import dummyAvatar from '@common/react/images/avatar.jpg';

import { useMobileView } from '@commonTuna/react/utils/useMobileView';

import { DoctorPortal } from '@app/objects/DoctorPortal';
import { User } from '@app/objects/User';
import SpecialtyTag from '@app/components/Pages/Specialties/SpecialtyTag';
import DoctorAvatar from '@app/components/UI/DoctorCard/DoctorAvatar';
import Certificates from '@app/components/Pages/DoctorPortal/Certificates';

interface Props {
	doctor: DoctorPortal;
	setScrollMargin: (value) => void;
	location: Location;
	user: User | null;
	appointmentId?: number;
	withoutNavMenu?: boolean;
}

const navMenu = [
	{
		title: 'About',
		check: (doctor: DoctorPortal) => !!doctor?.portalContentEn,
		id: 'about',
	},
	{
		title: 'Locations',
		check: (doctor: DoctorPortal) => true,
		id: 'locations',
		className: 'mobile-hide',
	},
	{
		title: 'Book Online',
		check: (doctor: DoctorPortal) => true,
		id: 'bookOnline',
		className: 'show-mobile show-mobile__inline-block',
	},
	{
		title: 'Reviews',
		check: (doctor: DoctorPortal) => true,
		id: 'reviews',
	},
];

const DoctorMenu: React.FC<Props> = ({
	doctor: content, setScrollMargin, withoutNavMenu = false, location, user, appointmentId,
}) => {
	const ref = React.useRef<any>(null);
	const isMobile = useMobileView(768);
	const [showOrigin, setShowOrigin] = React.useState(false);

	React.useEffect(() => {
		if (ref.current) {
			const resize = () => {
				const margin = ref.current?.getBoundingClientRect()?.height;
				setScrollMargin(margin + 125);
			};

			resize();
			window.addEventListener('resize', resize);

			return () => window.removeEventListener('resize', resize);
		}
	}, [ref.current]);

	React.useEffect(() => {
		setShowOrigin(isMobile);
	}, [isMobile]);
	const avatar = (showOrigin ? content.originalAvatar : content.avatar) || dummyAvatar;

	return (
		<div className="mb20 px15 main-content-block" ref={ref}>
			<div className="row">
				<div className="avatar left-pull">
					<DoctorAvatar
						key={(!showOrigin || !avatar) ? 'default' : 'origin'}
						hideWhenLoading
						withLink={false}
						alt={content.nameEn}
						src={avatar}
					/>
				</div>
				<div className="text-start left-pull main-info">
					{content && <div>
						<script type="application/ld+json" dangerouslySetInnerHTML={{ __html: `${JSON.stringify(content.doctorRatingInfo)}` }} />
						<h1 className="doctor-name">{content.nameEn}</h1>
						{content.certificates?.length ? <Certificates items={content.certificates} /> : ''}
					</div>}
					<div className="doctor-menu-content">
						<div>
							<div className="pull-left">
								{content.specialties?.length > 0 && content.specialties.filter((item) => item.specialty)
									.map((item) => <SpecialtyTag
										key={item.id}
										tagClassName="title__second"
										specialty={item.specialty}
									/>)}
							</div>
							{content.locations?.length > 0
								? <h2 className="title__third pull-right doctor-location inline-select-item inline-select-item_sm">
									{content.locations[0].location.portalPathEn
										? <NavLink to={`/clinic/${content.locations[0].location.portalPathEn}`}>
											{content.locations[0].location.nameEn}
										</NavLink>
										: content.locations[0].location.nameEn}
								</h2> : null}
						</div>
						{content && !withoutNavMenu
						&& <nav className="doctor-portal__nav pull-left">
							<ul>
								{navMenu.filter((item) => item.check(content)).map((item) =>
									<li key={item.id} className={item.className}>
										<a href={`${location?.pathname}#${item.id}`}>{item.title}</a>
									</li>)
								}
							</ul>
						</nav>}
					</div>
				</div>
			</div>
		</div>
	);
};

export default DoctorMenu;
